import Papa from 'papaparse';

const COMMON_ENCODINGS = ['utf-8', 'latin1', 'windows-1252'];
const CHUNK_SIZE = 1024 * 1024; // 1 MB per tongada en mode streaming

function readAsText(input, encoding) {
  if (typeof input === 'string') {
    return Promise.resolve(input);
  }
  if (input instanceof ArrayBuffer || ArrayBuffer.isView(input)) {
    const buffer = input instanceof ArrayBuffer ? input : input.buffer;
    return Promise.resolve(new TextDecoder(encoding).decode(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(input, encoding);
  });
}

function stripBom(value) {
  if (typeof value !== 'string' || value.length === 0) return value;
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}

function baseConfig(options) {
  return {
    header: false,
    skipEmptyLines: 'greedy',
    delimiter: options.delimiter || '',
    transform: (value) => (typeof value === 'string' ? value.trim() : value),
  };
}

function isDataRow(row) {
  return Array.isArray(row) && row.some((cell) => cell !== '' && cell != null);
}

function toHeaders(row) {
  return row.map((h, idx) => stripBom(String(h ?? `col_${idx + 1}`)));
}

function rowToCells(row, headers) {
  const out = new Array(headers.length).fill(null);
  for (let i = 0; i < headers.length; i++) {
    const v = row[i];
    out[i] = v === undefined || v === '' ? null : v;
  }
  return out;
}

export async function parseCsv(input, options = {}, onProgress = () => {}) {
  const encoding = options.encoding || 'utf-8';

  // STREAMING (File/Blob): PapaParse llegeix i parseja per tongades de forma
  // asíncrona, cedint el thread entre tongades → no bloqueja el navegador en
  // fitxers grans i podem emetre progrés (bytes processats / mida del fitxer).
  if (input instanceof Blob) {
    const size = input.size || 0;
    const rows = [];
    let headers = null;
    let delimiter = '';
    onProgress({ phase: 'parsing', processed: 0, total: size });
    await new Promise((resolve, reject) => {
      Papa.parse(input, {
        ...baseConfig(options),
        encoding,
        chunkSize: CHUNK_SIZE,
        chunk: (results) => {
          if (results.meta && results.meta.delimiter) delimiter = results.meta.delimiter;
          for (const row of results.data) {
            if (!isDataRow(row)) continue;
            if (headers === null) { headers = toHeaders(row); continue; }
            rows.push(rowToCells(row, headers));
          }
          if (size) {
            const cursor = results.meta && results.meta.cursor != null ? results.meta.cursor : size;
            onProgress({ phase: 'parsing', processed: Math.min(cursor, size), total: size });
          }
        },
        complete: () => resolve(),
        error: (err) => reject(err instanceof Error ? err : new Error(err && err.message ? err.message : 'Error analitzant el CSV')),
      });
    });
    onProgress({ phase: 'parsing', processed: size, total: size });
    if (headers === null) {
      return { headers: [], rows: [], encoding, sheetName: null, sheetNames: [], delimiter };
    }
    return { headers, rows, encoding, sheetName: null, sheetNames: [], delimiter };
  }

  // Path síncron (string/ArrayBuffer — p. ex. tests): es manté com abans.
  const text = await readAsText(input, encoding);
  const result = Papa.parse(text, baseConfig(options));
  const data = result.data.filter(isDataRow);
  if (data.length === 0) {
    return { headers: [], rows: [], encoding, sheetName: null, sheetNames: [], delimiter: result.meta.delimiter };
  }
  const headers = toHeaders(data.shift() || []);
  const rows = data.map((row) => rowToCells(row, headers));
  return { headers, rows, encoding, sheetName: null, sheetNames: [], delimiter: result.meta.delimiter };
}

// Detecció del separador a partir d'una mostra del fitxer: reutilitza el guess de
// PapaParse (el mateix que fa servir el parseig complet quan delimiter és buit).
// Serveix per mostrar a la pantalla de càrrega quin separador s'ha detectat; l'usuari
// el pot sobreescriure i llavors el parseig es fa amb el separador triat.
export async function detectDelimiter(input, options = {}) {
  const encoding = options.encoding || 'utf-8';
  const text = stripBom(await readAsText(input, encoding));
  const result = Papa.parse(text, { ...baseConfig({}), preview: 100 });
  return (result.meta && result.meta.delimiter) || '';
}

// Detecció del separador de MULTIRESPOSTA (dins d'una cel·la) a partir d'una mostra.
// Parseja amb el separador de camps (`options.delimiter`) i, a les cel·les resultants,
// compta per quin candidat (|, ;, ,) es parteixen en 2+ tokens no buits; torna el més
// freqüent (empat → prioritat a '|', el separador canònic), o '' si no hi ha senyal clar.
// Best-effort per mostrar-lo a la pantalla de càrrega; l'usuari el pot sobreescriure.
export async function detectMultiSeparator(input, options = {}) {
  const encoding = options.encoding || 'utf-8';
  const fieldSep = options.delimiter || '';
  const text = stripBom(await readAsText(input, encoding));
  const result = Papa.parse(text, { ...baseConfig({ delimiter: fieldSep }), preview: 200 });
  const rows = Array.isArray(result.data) ? result.data : [];
  const cands = ['|', ';', ','].filter((c) => c !== fieldSep);
  const hits = Object.fromEntries(cands.map((c) => [c, 0]));
  for (const row of rows) {
    if (!Array.isArray(row)) continue;
    for (const cell of row) {
      if (typeof cell !== 'string' || cell === '') continue;
      for (const c of cands) {
        if (cell.indexOf(c) !== -1 && cell.split(c).map((s) => s.trim()).filter(Boolean).length >= 2) hits[c] += 1;
      }
    }
  }
  let best = '';
  let bestN = 0;
  for (const c of cands) if (hits[c] > bestN) { best = c; bestN = hits[c]; } // ordre dels cands → empat cap a '|'
  return bestN >= 2 ? best : '';
}

export const csvSupportedEncodings = COMMON_ENCODINGS;
